import { useState } from "react";
import { buildersApi, ApiError } from "@/api";
import type { BuilderRecord, BuilderCreateInput, BuilderUpdateInput } from "@/types";
import { Button, TextField, TextAreaField } from "@/components/ui";
import { FormDrawer } from "../jobs/FormDrawer";

/** New / edit builder firm. Create sends only the non-empty fields; edit sends
 *  every field every time so a cleared box clears the column (the PATCH is
 *  presence-based — "" is a value, omitted is untouched). Company name is the
 *  one required field; a firm with no email can be attached to jobs but nobody
 *  can be invited until one is added. */
export function BuilderFormDrawer({
  builder,
  onClose,
  onSaved,
}: {
  /** Omit to create. */
  builder?: BuilderRecord;
  onClose: () => void;
  onSaved: (saved: BuilderRecord) => void;
}) {
  const editing = !!builder;
  const [company, setCompany] = useState(builder?.company_name ?? "");
  const [first, setFirst] = useState(builder?.contact_first_name ?? "");
  const [last, setLast] = useState(builder?.contact_last_name ?? "");
  const [role, setRole] = useState(builder?.contact_role ?? "");
  const [email, setEmail] = useState(builder?.email ?? "");
  const [phone, setPhone] = useState(builder?.phone ?? "");
  const [trade, setTrade] = useState(builder?.trade ?? "");
  const [website, setWebsite] = useState(builder?.website ?? "");
  const [notes, setNotes] = useState(builder?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit() {
    if (saving) return;
    if (!company.trim()) {
      setError("Enter the company name.");
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      if (editing) {
        const body: BuilderUpdateInput = {
          company_name: company.trim(),
          contact_first_name: first.trim(),
          contact_last_name: last.trim(),
          contact_role: role.trim(),
          email: email.trim(),
          phone: phone.trim(),
          trade: trade.trim(),
          website: website.trim(),
          notes: notes.trim(),
        };
        const res = await buildersApi.updateBuilder(builder.id, body);
        onSaved(res.builder);
      } else {
        const body: BuilderCreateInput = { company_name: company.trim() };
        const put = (key: keyof BuilderCreateInput, val: string) => {
          const t = val.trim();
          if (t) (body as unknown as Record<string, unknown>)[key] = t;
        };
        put("contact_first_name", first);
        put("contact_last_name", last);
        put("contact_role", role);
        put("email", email);
        put("phone", phone);
        put("trade", trade);
        put("website", website);
        put("notes", notes);
        const res = await buildersApi.createBuilder(body);
        onSaved(res.builder);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : editing ? "Could not save the builder." : "Could not create the builder.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title={editing ? "Edit builder" : "New builder"}
      busy={saving}
      onClose={onClose}
      closeOnOverlayClick={!editing}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>{editing ? "Save" : "Create builder"}</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <h3>Firm</h3>
        <TextField label="Company name" required value={company} onChange={(e) => setCompany(e.target.value)} />
        <div className="lead-drawer__two">
          <TextField label="Trade" value={trade} onChange={(e) => setTrade(e.target.value)} hint="e.g. General builder, Timber frame" />
          <TextField label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} />
        </div>
      </section>

      <section className="job-drawer__section">
        <h3>Primary contact</h3>
        <div className="lead-drawer__two">
          <TextField label="First name" value={first} onChange={(e) => setFirst(e.target.value)} />
          <TextField label="Last name" value={last} onChange={(e) => setLast(e.target.value)} />
        </div>
        <TextField label="Role" value={role} onChange={(e) => setRole(e.target.value)} hint="Optional — e.g. Owner, Site manager" />
        <div className="lead-drawer__two">
          <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} hint="The address an app invite goes to" />
          <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
      </section>

      <section className="job-drawer__section">
        <h3>Notes</h3>
        <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </section>
    </FormDrawer>
  );
}
